// Verifica multipla (lotti di telai): funzioni senza React, usate dalla pagina
// TakataVerificaMultiplaPage e coperte da test.
// Design: docs/superpowers/specs/2026-10-22-takata-verifica-multipla-design.md
import type { LottoTakata, VoceLotto } from '../services/takataService';
import { ESITO_LABEL, etichettaMarcaTakata } from '../services/takataService';

// I giri con portale esistono dalla 1.2.7 dell'estensione.
export const VERSIONE_ESTENSIONE_LOTTI = '1.2.7';

/** a >= b fra versioni «x.y.z». Una versione illeggibile non basta mai. */
export function versioneAlmeno(a: string | null | undefined, b: string): boolean {
  const pa = String(a || '').split('.').map((n) => parseInt(n, 10));
  const pb = b.split('.').map((n) => parseInt(n, 10));
  if (pa.length !== 3 || pa.some((n) => Number.isNaN(n))) return false;
  for (let i = 0; i < 3; i += 1) {
    if (pa[i] > pb[i]) return true;
    if (pa[i] < pb[i]) return false;
  }
  return true;
}

export type StatoEstensione = 'assente' | 'da_aggiornare' | 'pronta';

export function statoEstensione(): StatoEstensione {
  const radice = document.documentElement;
  if (!radice.hasAttribute('data-unasca-takata-ext')) return 'assente';
  return versioneAlmeno(radice.getAttribute('data-unasca-takata-ext-version'), VERSIONE_ESTENSIONE_LOTTI) ? 'pronta' : 'da_aggiornare';
}

export interface RispostaEstensione { ok?: boolean; error?: string; [k: string]: unknown }

/**
 * Richiesta all'estensione tramite il ponte (content-token.js): postMessage nella pagina,
 * risposta con lo stesso `richiestaId`. Senza risposta entro il tempo dato si considera
 * l'estensione non raggiungibile.
 */
export function chiediAEstensione(type: 'LOTTO_AVVIA' | 'LOTTO_FERMA' | 'LOTTO_STATO', extra: Record<string, unknown> = {}, attesaMs = 15000): Promise<RispostaEstensione> {
  return new Promise((risolvi) => {
    const richiestaId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const fine = (r: RispostaEstensione) => {
      window.removeEventListener('message', ascolta);
      clearTimeout(timer);
      risolvi(r);
    };
    const ascolta = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const d = event.data;
      if (!d || d.source !== 'unasca-takata-ext' || d.type !== 'LOTTO_RISPOSTA' || d.richiestaId !== richiestaId) return;
      fine(d);
    };
    const timer = setTimeout(() => fine({ ok: false, error: 'L\'estensione non risponde: ricarica la pagina e riprova.' }), attesaMs);
    window.addEventListener('message', ascolta);
    window.postMessage({ source: 'unasca-takata-card', type, richiestaId, ...extra }, window.location.origin);
  });
}

// Il giro dell'estensione e' «interrotto» se il battito manca da piu' di 7 minuti (il
// telaio ne concede uno e mezzo, piu' 1 se la registrazione e' gia' partita; le copie fino
// alla 1.2.7 ne concedevano 6): browser chiuso, estensione ricaricata, computer spento.
export const BATTITO_SCADUTO_MS = 7 * 60 * 1000;

export function giroInterrotto(lotto: Pick<LottoTakata, 'esecuzione'>, ora = Date.now()): boolean {
  const e = lotto.esecuzione;
  if (!e || !e.attiva) return false;
  const t = e.aggiornataIl ? new Date(e.aggiornataIl).getTime() : 0;
  return !t || ora - t > BATTITO_SCADUTO_MS;
}

export function contaGiro(voci: VoceLotto[], giro: 'server' | 'automatico' | 'manuale'): number {
  return voci.filter((v) => {
    if (v.stato === 'fatto') return false;
    if (giro === 'server') return v.gruppo === 'server';
    if (giro === 'automatico') return v.gruppo === 'automatico' && (v.stato === 'in_attesa' || v.stato === 'in_corso');
    return v.gruppo === 'manuale' || (v.gruppo === 'automatico' && (v.stato === 'errore' || v.stato === 'saltato'));
  }).length;
}

function fmtData(d?: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

export const STATO_VOCE_LABEL: Record<VoceLotto['stato'], string> = {
  in_attesa: 'Da verificare',
  in_corso: 'In corso',
  fatto: 'Verificato',
  errore: 'Da rifare',
  saltato: 'Saltato',
};

/** Testo leggibile dell'esito di una voce, anche quando l'esito non c'e' ancora. */
export function esitoVoce(v: VoceLotto): string {
  if (v.stato === 'fatto' && v.esito) return ESITO_LABEL[v.esito] || v.esito;
  if (v.gruppo === 'non_disponibile') return 'Non verificabile';
  return STATO_VOCE_LABEL[v.stato];
}

function cellaCsv(valore: string): string {
  const s = String(valore ?? '');
  // Una cella che comincia con = + - @ verrebbe eseguita da Excel come formula.
  const sicura = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[";\r\n]/.test(sicura) ? `"${sicura.replace(/"/g, '""')}"` : sicura;
}

/**
 * Elenco degli esiti in CSV per Excel italiano: separatore «;» e BOM UTF-8 (senza, Excel
 * legge male le lettere accentate).
 */
export function csvLotto(lotto: Pick<LottoTakata, 'voci'>): string {
  const righe = [['Telaio', 'Marca', 'Esito', 'Protocollo', 'Data verifica', 'Note']];
  for (const v of lotto.voci) {
    righe.push([
      v.vin,
      etichettaMarcaTakata(v.marca),
      esitoVoce(v),
      v.verifica ? v.verifica.protocollo : '',
      v.verifica ? fmtData(v.verifica.createdAt) : '',
      v.stato === 'fatto' ? '' : v.messaggio,
    ]);
  }
  return `﻿${righe.map((r) => r.map(cellaCsv).join(';')).join('\r\n')}\r\n`;
}
